<script setup lang="ts">
import wait from '@/modules/common/services/wait';
import { errorString } from '@/utils/helpers/rest-response';
import { computed, onMounted, shallowRef } from 'vue';
import { store as vuex } from '@/store/store';
import { type VOtpInput } from 'vuetify/lib/components';
import { useRules } from '@/composables/vuetify-validation';

const emit = defineEmits<{ (event: 'close-modal'): void }>();

const cmpOTPInput = shallowRef<null | { focus(): void }>(null);

const loginState = computed(() => vuex.state.loginState!);
const issuer = computed(() => loginState.value.tfa?.tfaIssuer ?? '');
const accountName = computed(() =>
  loginState.value.tfa
    ? `${loginState.value.tfa.tfaIssuer}:${loginState.value.tfa.tfaIssuerName}`
    : ''
);
const issuerSecret = computed(() => loginState.value.tfa?.tfaIssuerSecret ?? '');
const qrCode = computed(
  () =>
    `otpauth://totp/${accountName.value}?secret=${issuerSecret.value}&amp;issuer=${issuer.value}`
);
const has2faEnabled = computed(() => !!loginState.value.tfa?.tfaIsEnabled);

const otp = shallowRef('');
const countProcessing = shallowRef(0);
const showSuccess = shallowRef(false);

function fetchCurrent2FA() {
  return vuex.dispatch('fetchCurrent2FA');
}

function closeDialog(): void {
  emit('close-modal');
}

function focusOtpInput(): void {
  cmpOTPInput.value?.focus();
}

onMounted(() => {
  focusOtpInput();
  void fetchCurrent2FA();
});

const { rules, validate, isSubmitted } = useRules<{ otp: string }>({
  otp: {
    rules: [
      (v) => (typeof v === 'string' ? !!v.trim() : !!v) || 'Please enter a code to continue.',
      (v, { isTargetedField }) =>
        (!isTargetedField && !isSubmitted.value) ||
        v?.length === 6 ||
        'Please enter a 6 digit code.',
      (v) => {
        if (v?.length !== 6) return true;
        // we always want to eagerly run the validation, once we have all the digits
        ++countProcessing.value;
        return vuex
          .dispatch('enable2FALogin', v)
          .then(async () => {
            // success, alert user and then close form
            showSuccess.value = true;

            // reload 2fa status in the background
            void fetchCurrent2FA();

            await wait(800);
            closeDialog();

            return true as const;
          })
          .catch((err) => {
            focusOtpInput();
            return errorString(err);
          })
          .finally(() => --countProcessing.value);
      },
    ],
  },
});

defineExpose({
  test: {
    setOTP: (v: string) => (otp.value = v),
  },
});
</script>

<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    light
    max-width="700"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
    @click:outside="closeDialog()"
    @keydown.esc="closeDialog()"
    @shortkey.native="closeDialog()"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>Enable 2FA</VCardTitle>
        <VCardText v-if="has2faEnabled && !countProcessing">
          Two-Factor Authentication is already enabled.
        </VCardText>
        <VCardText v-else>
          <p>
            {{ $t('doEnable2FA.text') }}
          </p>
          <VRow>
            <VCol class="pa-0" cols="5">
              <QRCode :options="{ width: 250 }" :value="qrCode" />
            </VCol>
            <VCol class="pt-5">
              <p>{{ $t('registerManual2FA.title') }}</p>
              <p>{{ $t('registerManual2FA.text') }}</p>
              <p>{{ $t('registerAccount2FA.text', { account: accountName }) }}</p>
              <p>{{ $t('registerKey2FA.text', { key: issuerSecret }) }}</p>
              <p>{{ $t('registerTimeBased2FA.text') }}</p>
            </VCol>
          </VRow>
          <VRow>
            <VCol class="pt-8">
              <p>Enter the 6-digit code from your app:</p>
              <VOtpInput
                :ref="(r) => (cmpOTPInput = r)"
                v-model="otp"
                :disabled="!!countProcessing"
                length="6"
                plain
                type="number"
                v-bind="rules.otp"
                @blur="validate('otp')"
              />
              <label class="error--text">{{ rules.otp.errorMessages.join('\n') }}</label>
            </VCol>
          </VRow>
          <VRow v-if="showSuccess">
            <VCol class="pa-0 px-1 col-6 offset-3">
              <div class="v-alert v-alert--dense text--primary text-body-2 text-center success">
                Two-Factor Authentication has been enabled.
              </div>
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions>
          <VBtn color="secondary" data-test="cancel-btn" @click="closeDialog">
            {{ $tc('dialogs.cancelButton') }}
          </VBtn>
          <VSpacer />
          <VBtn
            class="px-6"
            color="primary"
            :disabled="!!countProcessing || has2faEnabled"
            type="submit"
            @click="validate()"
          >
            Enable 2FA
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>
